import React from 'react';
import { User, Camera, Loader2 } from 'lucide-react';
import { PatientInfo } from '../types';
import { CollapsibleCard, CollapseControl } from './CollapsibleCard';

interface PatientInfoFormProps {
  patientInfo: PatientInfo;
  onChange: (field: keyof PatientInfo, value: string) => void;
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  loading: boolean;
  loadingMessage: string;
  /** Sterowanie zwijaniem karty „Dane Pacjenta" (opcjonalne — bez niego karta jest uncontrolled). */
  patientCard?: CollapseControl;
  /** Sterowanie zwijaniem karty „Wczytaj Wyniki przez OCR". */
  ocrCard?: CollapseControl;
}

export const PatientInfoForm: React.FC<PatientInfoFormProps> = ({
  patientInfo,
  onChange,
  onImageUpload,
  loading,
  loadingMessage,
  patientCard,
  ocrCard
}) => {
  const isOCR = loading && loadingMessage.toLowerCase().includes('ocr');

  return (
    <div className="flex flex-col gap-4">
      {/* Patient Info */}
      <CollapsibleCard
        title="Dane Pacjenta"
        icon={User}
        open={patientCard?.open}
        onOpenChange={patientCard?.onOpenChange}
      >
        <div className="grid grid-cols-2 gap-3.5">
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-txt-secondary mb-1.5">
              Imię i nazwisko
            </label>
            <input
              type="text"
              value={patientInfo.name}
              onChange={(e) => onChange('name', e.target.value)}
              placeholder="np. Jan Kowalski"
              className="w-full bg-surface-card border border-brd rounded-lg px-3 py-2 text-sm text-txt-primary placeholder-txt-tertiary focus:outline-none focus:border-accent focus:ring-4 focus:ring-emerald-500/5 transition-colors duration-200 focus-glass-glow"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-txt-secondary mb-1.5">
              Data urodzenia
            </label>
            <input
              type="date"
              value={patientInfo.birthDate}
              onChange={(e) => onChange('birthDate', e.target.value)}
              className="w-full bg-surface-card border border-brd rounded-lg px-3 py-2 text-sm text-txt-primary focus:outline-none focus:border-accent focus:ring-4 focus:ring-emerald-500/5 transition-colors duration-200 focus-glass-glow"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-txt-secondary mb-1.5">
              Data badania
            </label>
            <input
              type="date"
              value={patientInfo.testDate}
              onChange={(e) => onChange('testDate', e.target.value)}
              className="w-full bg-surface-card border border-brd rounded-lg px-3 py-2 text-sm text-txt-primary focus:outline-none focus:border-accent focus:ring-4 focus:ring-emerald-500/5 transition-colors duration-200 focus-glass-glow"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-txt-secondary mb-1.5">
              Badający
            </label>
            <input
              type="text"
              value={patientInfo.examiner}
              onChange={(e) => onChange('examiner', e.target.value)}
              placeholder="mgr Janusz Psycholog"
              className="w-full bg-surface-card border border-brd rounded-lg px-3 py-2 text-sm text-txt-primary placeholder-txt-tertiary focus:outline-none focus:border-accent focus:ring-4 focus:ring-emerald-500/5 transition-colors duration-200 focus-glass-glow"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-txt-secondary mb-1.5">
              Instytucja
            </label>
            <input
              type="text"
              value={patientInfo.institution || ''}
              onChange={(e) => onChange('institution', e.target.value)}
              placeholder="Poradnia Psychologiczna"
              className="w-full bg-surface-card border border-brd rounded-lg px-3 py-2 text-sm text-txt-primary placeholder-txt-tertiary focus:outline-none focus:border-accent focus:ring-4 focus:ring-emerald-500/5 transition-colors duration-200 focus-glass-glow"
            />
          </div>
        </div>
      </CollapsibleCard>

      {/* OCR Quick Fill */}
      <CollapsibleCard
        title="Wczytaj Wyniki przez OCR"
        icon={Camera}
        open={ocrCard?.open}
        onOpenChange={ocrCard?.onOpenChange}
      >
        <div className="flex flex-col gap-3">
          <p className="text-xs text-txt-secondary leading-relaxed">
            Wgraj zdjęcie lub skan protokołu WISC-V. Autorski algorytm OCR odczyta wyniki w kilka sekund.
          </p>

          <label className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-4 cursor-pointer transition-colors duration-300 text-center ${
            loading
              ? 'border-brd bg-surface-subtle cursor-not-allowed opacity-50'
              : 'border-accent-border bg-emerald-50/10 hover:bg-emerald-50/30 dark:bg-emerald-950/20 dark:hover:bg-emerald-950/40'
          }`}>
            {isOCR ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 text-accent animate-spin" />
                <span className="text-xs font-semibold text-accent-strong animate-pulse">Trwa analizowanie obrazu...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1.5 group">
                <Camera className="w-7 h-7 text-accent group-hover:text-accent-strong transition-colors duration-200" />
                <span className="text-xs font-semibold text-accent-strong">Wgraj zdjęcie protokołu</span>
                <span className="text-[11px] text-txt-tertiary">JPG, PNG lub PDF</span>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={onImageUpload}
              disabled={loading}
              className="hidden"
            />
          </label>

          {isOCR && (
            <div className="text-[11px] text-accent bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-2 flex items-center gap-2 mt-1 animate-pulse">
              <span className="w-1.5 h-1.5 rounded-full bg-accent" />
              <span>{loadingMessage}</span>
            </div>
          )}
        </div>
      </CollapsibleCard>
    </div>
  );
};
